import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import NoDoublesLogo from '@/components/marketing/NoDoublesLogo';

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

export default function ForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    await supabase.auth.resetPasswordForEmail(values.email.toLowerCase().trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    // Always show success to avoid leaking account existence
    setEmailSent(true);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface to-muted flex flex-col justify-start md:justify-center px-4 py-6 md:py-12">
      <div className="w-full max-w-md mx-auto">
        <Link
          to="/auth"
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to sign in</span>
        </Link>

        <div className="text-center mb-8">
          <NoDoublesLogo size={80} className="mx-auto mb-4" />
        </div>

        <div className="bg-card rounded-2xl shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-sun-gold to-fairway-light" />

          <div className="p-8 md:p-10">
            {emailSent ? (
              <div className="text-center space-y-4">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Check your email</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  If an account exists with that email address, we've sent a password reset link.
                  Please check your inbox and spam folder.
                </p>
                <div className="pt-4">
                  <Link to="/auth">
                    <Button variant="outline" className="w-full h-12">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to sign in
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Reset your password</h2>
                  <p className="text-muted-foreground text-sm">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">
                            Email address
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                              <Input
                                {...field}
                                type="email"
                                placeholder="you@example.com"
                                className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                disabled={isLoading}
                                autoFocus
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 bg-gradient-primary hover:shadow-green transition-all duration-200 text-primary-foreground"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>
                  </form>
                </Form>

                <div className="text-center pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Remember your password?{' '}
                    <Link
                      to="/auth"
                      className="text-primary font-medium hover:text-primary-hover transition-colors"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
